import React, { useState, lazy } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { SearchIcon, MailIcon, LockIcon, AlertCircleIcon } from 'lucide-react';
const colorTokens = [{
  name: 'background',
  var: '--background',
  class: 'bg-background'
}, {
  name: 'foreground',
  var: '--foreground',
  class: 'bg-foreground'
}, {
  name: 'muted',
  var: '--muted',
  class: 'bg-muted'
}, {
  name: 'muted-foreground',
  var: '--muted-foreground',
  class: 'bg-muted-foreground'
}, {
  name: 'popover',
  var: '--popover',
  class: 'bg-popover'
}, {
  name: 'popover-foreground',
  var: '--popover-foreground',
  class: 'bg-popover-foreground'
}, {
  name: 'card',
  var: '--card',
  class: 'bg-card'
}, {
  name: 'card-foreground',
  var: '--card-foreground',
  class: 'bg-card-foreground'
}, {
  name: 'border',
  var: '--border',
  class: 'bg-border'
}, {
  name: 'input',
  var: '--input',
  class: 'bg-input'
}, {
  name: 'primary',
  var: '--primary',
  class: 'bg-primary'
}, {
  name: 'primary-foreground',
  var: '--primary-foreground',
  class: 'bg-primary-foreground'
}, {
  name: 'secondary',
  var: '--secondary',
  class: 'bg-secondary'
}, {
  name: 'secondary-foreground',
  var: '--secondary-foreground',
  class: 'bg-secondary-foreground'
}, {
  name: 'accent',
  var: '--accent',
  class: 'bg-accent'
}, {
  name: 'accent-foreground',
  var: '--accent-foreground',
  class: 'bg-accent-foreground'
}, {
  name: 'destructive',
  var: '--destructive',
  class: 'bg-destructive'
}, {
  name: 'destructive-foreground',
  var: '--destructive-foreground',
  class: 'bg-destructive-foreground'
}, {
  name: 'ring',
  var: '--ring',
  class: 'bg-ring'
}];
const buttonVariants = ['default', 'destructive', 'outline', 'secondary', 'ghost', 'link', 'ios', 'tiktok'] as const;
const buttonSizes = ['sm', 'default', 'lg', 'icon'] as const;
export function StyleGuide() {
  const [isDark, setIsDark] = useState(false);
  const toggleDarkMode = () => {
    setIsDark(!isDark);
    document.documentElement.classList.toggle('dark', !isDark);
  };
  return <div className="min-h-screen bg-background text-foreground" data-id="element-2640">
      <div className="max-w-6xl mx-auto px-6 py-12" data-id="element-2641">
        {/* Header */}
        <div className="mb-12" data-id="element-2642">
          <div className="flex items-center justify-between mb-4" data-id="element-2643">
            <h1 className="text-4xl font-bold" data-id="element-2644">Design System</h1>
            <Button variant="outline" onClick={toggleDarkMode} data-id="element-2645">
              {isDark ? '☀️ Light Mode' : '🌙 Dark Mode'}
            </Button>
          </div>
          <p className="text-muted-foreground text-lg" data-id="element-2646">
            ScribeTok component library and design tokens reference.
          </p>
        </div>

        {/* Color Tokens */}
        <section className="mb-16" data-id="element-2647">
          <h2 className="text-2xl font-semibold mb-2" data-id="element-2648">Color Tokens</h2>
          <p className="text-muted-foreground mb-6" data-id="element-2649">
            CSS custom properties from index.css. Toggle dark mode to see how
            they change.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-id="element-2650">
            {colorTokens.map(token => <div key={token.name} className="flex flex-col" data-id="element-2651">
                <div className={`h-16 rounded-lg border border-border ${token.class}`} title={`var(${token.var})`} data-id="element-2652" />
                <p className="text-xs font-mono mt-2 text-foreground" data-id="element-2653">
                  {token.name}
                </p>
                <p className="text-xs font-mono text-muted-foreground" data-id="element-2654">
                  {token.var}
                </p>
              </div>)}
          </div>
        </section>

        {/* Typography */}
        <section className="mb-16" data-id="element-2655">
          <h2 className="text-2xl font-semibold mb-2" data-id="element-2656">Typography</h2>
          <p className="text-muted-foreground mb-6" data-id="element-2657">
            Heading and body text scales using Tailwind defaults.
          </p>
          <div className="space-y-6 bg-card border border-border rounded-xl p-6" data-id="element-2658">
            <div data-id="element-2659">
              <p className="text-xs text-muted-foreground mb-1" data-id="element-2660">
                text-6xl / 60px
              </p>
              <p className="text-6xl font-bold" data-id="element-2661">Heading 1</p>
            </div>
            <div data-id="element-2662">
              <p className="text-xs text-muted-foreground mb-1" data-id="element-2663">
                text-5xl / 48px
              </p>
              <p className="text-5xl font-bold" data-id="element-2664">Heading 2</p>
            </div>
            <div data-id="element-2665">
              <p className="text-xs text-muted-foreground mb-1" data-id="element-2666">
                text-4xl / 36px
              </p>
              <p className="text-4xl font-bold" data-id="element-2667">Heading 3</p>
            </div>
            <div data-id="element-2668">
              <p className="text-xs text-muted-foreground mb-1" data-id="element-2669">
                text-3xl / 30px
              </p>
              <p className="text-3xl font-semibold" data-id="element-2670">Heading 4</p>
            </div>
            <div data-id="element-2671">
              <p className="text-xs text-muted-foreground mb-1" data-id="element-2672">
                text-2xl / 24px
              </p>
              <p className="text-2xl font-semibold" data-id="element-2673">Heading 5</p>
            </div>
            <div data-id="element-2674">
              <p className="text-xs text-muted-foreground mb-1" data-id="element-2675">
                text-xl / 20px
              </p>
              <p className="text-xl font-medium" data-id="element-2676">Heading 6</p>
            </div>
            <div data-id="element-2677">
              <p className="text-xs text-muted-foreground mb-1" data-id="element-2678">
                text-base / 16px
              </p>
              <p className="text-base" data-id="element-2679">
                Body text — The quick brown fox jumps over the lazy dog.
              </p>
            </div>
            <div data-id="element-2680">
              <p className="text-xs text-muted-foreground mb-1" data-id="element-2681">
                text-sm / 14px
              </p>
              <p className="text-sm" data-id="element-2682">
                Small text — The quick brown fox jumps over the lazy dog.
              </p>
            </div>
            <div data-id="element-2683">
              <p className="text-xs text-muted-foreground mb-1" data-id="element-2684">
                text-xs / 12px
              </p>
              <p className="text-xs" data-id="element-2685">
                Extra small — The quick brown fox jumps over the lazy dog.
              </p>
            </div>
            <div data-id="element-2686">
              <p className="text-xs text-muted-foreground mb-1" data-id="element-2687">
                text-muted-foreground
              </p>
              <p className="text-muted-foreground" data-id="element-2688">
                Muted text for secondary content and descriptions.
              </p>
            </div>
          </div>
        </section>

        {/* Buttons */}
        <section className="mb-16" data-id="element-2689">
          <h2 className="text-2xl font-semibold mb-2" data-id="element-2690">Buttons</h2>
          <p className="text-muted-foreground mb-6" data-id="element-2691">
            All button variants and sizes. Note:{' '}
            <code className="bg-muted px-1 rounded" data-id="element-2692">default</code> and{' '}
            <code className="bg-muted px-1 rounded" data-id="element-2693">ios</code> variants are
            currently identical.
          </p>

          {/* Variants */}
          <div className="mb-8" data-id="element-2694">
            <h3 className="text-lg font-medium mb-4" data-id="element-2695">
              Variants (default size)
            </h3>
            <div className="flex flex-wrap gap-4 bg-card border border-border rounded-xl p-6" data-id="element-2696">
              {buttonVariants.map(variant => <div key={variant} className="flex flex-col items-center gap-2" data-id="element-2697">
                  <Button variant={variant} data-id="element-2698">
                    {variant === 'icon' ? '★' : variant}
                  </Button>
                  <span className="text-xs text-muted-foreground font-mono" data-id="element-2699">
                    {variant}
                  </span>
                </div>)}
            </div>
          </div>

          {/* Sizes */}
          <div className="mb-8" data-id="element-2700">
            <h3 className="text-lg font-medium mb-4" data-id="element-2701">
              Sizes (default variant)
            </h3>
            <div className="flex flex-wrap items-end gap-4 bg-card border border-border rounded-xl p-6" data-id="element-2702">
              {buttonSizes.map(size => <div key={size} className="flex flex-col items-center gap-2" data-id="element-2703">
                  <Button size={size} data-id="element-2704">
                    {size === 'icon' ? '★' : `Size ${size}`}
                  </Button>
                  <span className="text-xs text-muted-foreground font-mono" data-id="element-2705">
                    {size}
                  </span>
                </div>)}
            </div>
          </div>

          {/* States */}
          <div data-id="element-2706">
            <h3 className="text-lg font-medium mb-4" data-id="element-2707">States</h3>
            <div className="flex flex-wrap gap-4 bg-card border border-border rounded-xl p-6" data-id="element-2708">
              <div className="flex flex-col items-center gap-2" data-id="element-2709">
                <Button data-id="element-2710">Normal</Button>
                <span className="text-xs text-muted-foreground" data-id="element-2711">Normal</span>
              </div>
              <div className="flex flex-col items-center gap-2" data-id="element-2712">
                <Button disabled data-id="element-2713">Disabled</Button>
                <span className="text-xs text-muted-foreground" data-id="element-2714">Disabled</span>
              </div>
              <div className="flex flex-col items-center gap-2" data-id="element-2715">
                <Button isLoading data-id="element-2716">Loading</Button>
                <span className="text-xs text-muted-foreground" data-id="element-2717">Loading</span>
              </div>
            </div>
          </div>
        </section>

        {/* Cards */}
        <section className="mb-16" data-id="element-2718">
          <h2 className="text-2xl font-semibold mb-2" data-id="element-2719">Cards</h2>
          <p className="text-muted-foreground mb-6" data-id="element-2720">
            Card component with header, content, and footer subcomponents.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2721">
            <Card data-id="element-2722">
              <CardHeader data-id="element-2723">
                <CardTitle data-id="element-2724">Card Title</CardTitle>
                <CardDescription data-id="element-2725">
                  Card description goes here with supporting text.
                </CardDescription>
              </CardHeader>
              <CardContent data-id="element-2726">
                <p data-id="element-2727">
                  Card content area. This is where the main content of the card
                  lives.
                </p>
              </CardContent>
              <CardFooter className="gap-2" data-id="element-2728">
                <Button variant="outline" size="sm" data-id="element-2729">
                  Cancel
                </Button>
                <Button size="sm" data-id="element-2730">Save</Button>
              </CardFooter>
            </Card>

            <Card data-id="element-2731">
              <CardContent className="pt-6" data-id="element-2732">
                <p className="text-muted-foreground" data-id="element-2733">
                  Simple card with just content, no header or footer.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Inputs */}
        <section className="mb-16" data-id="element-2734">
          <h2 className="text-2xl font-semibold mb-2" data-id="element-2735">Inputs</h2>
          <p className="text-muted-foreground mb-6" data-id="element-2736">
            Input component with icon and error state support.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-card border border-border rounded-xl p-6" data-id="element-2737">
            <div className="space-y-2" data-id="element-2738">
              <label className="text-sm font-medium" data-id="element-2739">Default</label>
              <Input placeholder="Enter text..." data-id="element-2740" />
            </div>
            <div className="space-y-2" data-id="element-2741">
              <label className="text-sm font-medium" data-id="element-2742">With Icon</label>
              <Input placeholder="Search..." icon={<SearchIcon size={16} data-id="element-2744" />} data-id="element-2743" />
            </div>
            <div className="space-y-2" data-id="element-2745">
              <label className="text-sm font-medium" data-id="element-2746">Error State</label>
              <Input placeholder="Invalid input" error data-id="element-2747" />
              <p className="text-xs text-destructive" data-id="element-2748">
                This field is required.
              </p>
            </div>
            <div className="space-y-2" data-id="element-2749">
              <label className="text-sm font-medium" data-id="element-2750">Disabled</label>
              <Input placeholder="Disabled input" disabled data-id="element-2751" />
            </div>
            <div className="space-y-2" data-id="element-2752">
              <label className="text-sm font-medium" data-id="element-2753">Email with Icon</label>
              <Input type="email" placeholder="you@example.com" icon={<MailIcon size={16} data-id="element-2755" />} data-id="element-2754" />
            </div>
            <div className="space-y-2" data-id="element-2756">
              <label className="text-sm font-medium" data-id="element-2757">Password with Icon</label>
              <Input type="password" placeholder="••••••••" icon={<LockIcon size={16} data-id="element-2759" />} data-id="element-2758" />
            </div>
          </div>
        </section>

        {/* Custom Classes */}
        <section className="mb-16" data-id="element-2760">
          <h2 className="text-2xl font-semibold mb-2" data-id="element-2761">Custom Classes</h2>
          <p className="text-muted-foreground mb-6" data-id="element-2762">
            Project-specific CSS classes defined outside the design system.
          </p>
          <div className="bg-card border border-border rounded-xl p-6 space-y-6" data-id="element-2763">
            <div data-id="element-2764">
              <h3 className="text-lg font-medium mb-3" data-id="element-2765">tiktok-gradient</h3>
              <p className="text-sm text-muted-foreground mb-3" data-id="element-2766">
                Used for user message bubbles in SMS demos. Should be a pink/red
                gradient.
              </p>
              <div className="flex gap-4 items-center" data-id="element-2767">
                <div className="tiktok-gradient text-white px-6 py-3 rounded-2xl" data-id="element-2768">
                  tiktok-gradient
                </div>
                <code className="text-xs bg-muted px-2 py-1 rounded font-mono" data-id="element-2769">
                  .tiktok-gradient
                </code>
              </div>
            </div>
            <div data-id="element-2770">
              <h3 className="text-lg font-medium mb-3" data-id="element-2771">
                Message Bubble Pattern
              </h3>
              <p className="text-sm text-muted-foreground mb-3" data-id="element-2772">
                User bubbles use tiktok-gradient, system bubbles use
                bg-foreground text-background.
              </p>
              <div className="max-w-sm space-y-3" data-id="element-2773">
                <div className="flex justify-end" data-id="element-2774">
                  <div className="tiktok-gradient text-white px-4 py-2.5 rounded-2xl text-sm" data-id="element-2775">
                    User message (tiktok-gradient)
                  </div>
                </div>
                <div className="flex justify-start" data-id="element-2776">
                  <div className="bg-foreground text-background px-4 py-2.5 rounded-2xl text-sm" data-id="element-2777">
                    System response (bg-foreground)
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Spacing & Radius */}
        <section className="mb-16" data-id="element-2778">
          <h2 className="text-2xl font-semibold mb-2" data-id="element-2779">Spacing & Radius</h2>
          <p className="text-muted-foreground mb-6" data-id="element-2780">
            Border radius tokens from the design system.
          </p>
          <div className="bg-card border border-border rounded-xl p-6" data-id="element-2781">
            <div className="flex flex-wrap gap-6" data-id="element-2782">
              <div className="flex flex-col items-center gap-2" data-id="element-2783">
                <div className="w-16 h-16 bg-primary rounded-sm" data-id="element-2784" />
                <span className="text-xs text-muted-foreground font-mono" data-id="element-2785">
                  rounded-sm
                </span>
                <span className="text-xs text-muted-foreground" data-id="element-2786">
                  calc(--radius - 4px)
                </span>
              </div>
              <div className="flex flex-col items-center gap-2" data-id="element-2787">
                <div className="w-16 h-16 bg-primary rounded-md" data-id="element-2788" />
                <span className="text-xs text-muted-foreground font-mono" data-id="element-2789">
                  rounded-md
                </span>
                <span className="text-xs text-muted-foreground" data-id="element-2790">
                  calc(--radius - 2px)
                </span>
              </div>
              <div className="flex flex-col items-center gap-2" data-id="element-2791">
                <div className="w-16 h-16 bg-primary rounded-lg" data-id="element-2792" />
                <span className="text-xs text-muted-foreground font-mono" data-id="element-2793">
                  rounded-lg
                </span>
                <span className="text-xs text-muted-foreground" data-id="element-2794">
                  --radius (0.5rem)
                </span>
              </div>
              <div className="flex flex-col items-center gap-2" data-id="element-2795">
                <div className="w-16 h-16 bg-primary rounded-xl" data-id="element-2796" />
                <span className="text-xs text-muted-foreground font-mono" data-id="element-2797">
                  rounded-xl
                </span>
                <span className="text-xs text-muted-foreground" data-id="element-2798">0.75rem</span>
              </div>
              <div className="flex flex-col items-center gap-2" data-id="element-2799">
                <div className="w-16 h-16 bg-primary rounded-2xl" data-id="element-2800" />
                <span className="text-xs text-muted-foreground font-mono" data-id="element-2801">
                  rounded-2xl
                </span>
                <span className="text-xs text-muted-foreground" data-id="element-2802">1rem</span>
              </div>
              <div className="flex flex-col items-center gap-2" data-id="element-2803">
                <div className="w-16 h-16 bg-primary rounded-full" data-id="element-2804" />
                <span className="text-xs text-muted-foreground font-mono" data-id="element-2805">
                  rounded-full
                </span>
                <span className="text-xs text-muted-foreground" data-id="element-2806">9999px</span>
              </div>
            </div>
          </div>
        </section>

        {/* Notes */}
        <section className="mb-16" data-id="element-2807">
          <h2 className="text-2xl font-semibold mb-2" data-id="element-2808">Design Notes</h2>
          <div className="bg-muted border border-border rounded-xl p-6 space-y-4" data-id="element-2809">
            <div className="flex items-start gap-3" data-id="element-2810">
              <AlertCircleIcon size={20} className="text-destructive mt-0.5 flex-shrink-0" data-id="element-2811" />
              <div data-id="element-2812">
                <p className="font-medium" data-id="element-2813">Button variants: default = ios</p>
                <p className="text-sm text-muted-foreground" data-id="element-2814">
                  The{' '}
                  <code className="bg-background px-1 rounded" data-id="element-2815">default</code>{' '}
                  and <code className="bg-background px-1 rounded" data-id="element-2816">ios</code>{' '}
                  variants use identical classes. Consider removing one or
                  differentiating them.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3" data-id="element-2817">
              <AlertCircleIcon size={20} className="text-destructive mt-0.5 flex-shrink-0" data-id="element-2818" />
              <div data-id="element-2819">
                <p className="font-medium" data-id="element-2820">Dark mode primary flip</p>
                <p className="text-sm text-muted-foreground" data-id="element-2821">
                  In light mode,{' '}
                  <code className="bg-background px-1 rounded" data-id="element-2822">--primary</code>{' '}
                  is dark navy. In dark mode, it flips to near-white. This is
                  intentional for contrast but can be surprising.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3" data-id="element-2823">
              <AlertCircleIcon size={20} className="text-destructive mt-0.5 flex-shrink-0" data-id="element-2824" />
              <div data-id="element-2825">
                <p className="font-medium" data-id="element-2826">tiktok-gradient undefined?</p>
                <p className="text-sm text-muted-foreground" data-id="element-2827">
                  If the tiktok-gradient class shows no styling, it may not be
                  defined in index.css. Add it to @layer components.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3" data-id="element-2828">
              <AlertCircleIcon size={20} className="text-destructive mt-0.5 flex-shrink-0" data-id="element-2829" />
              <div data-id="element-2830">
                <p className="font-medium" data-id="element-2831">
                  Buttons use rounded-full, Inputs use rounded-xl
                </p>
                <p className="text-sm text-muted-foreground" data-id="element-2832">
                  This is an intentional design choice but worth noting for
                  consistency discussions.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="text-center text-sm text-muted-foreground border-t border-border pt-8" data-id="element-2833">
          <p data-id="element-2834">
            ScribeTok Design System • Last updated:{' '}
            {new Date().toLocaleDateString()}
          </p>
        </footer>
      </div>
    </div>;
}